import {
  each,
  concat,
  reject,
  uniq,
  get,
  map,
  filter,
  isEmpty,
  find,
  includes,
  sortBy,
  flatMap,
  difference,
  indexOf
} from 'lodash'
import axios from 'axios'
import moment from 'moment'
import humanizeString from 'humanize-string'
import * as momentZone from 'moment-timezone'

import { saveLocalStorageItem } from '../../utilities/LocalStorage'
import { cloudhelpPatch, cloudhelpGet } from '../../utilities/CloudHelp'

import { csrfToken } from 'Utilities/Csrf'
import parseTimeToEstimationTime from '../../utilities/TaskTimeEstimation'

// eslint-disable-next-line import/prefer-default-export
export const handlers = {
  bind (component) {
    const handlerFunctions = [
      'calendar_onNavigate',
      'calendar_onSelectEvent',
      'calendar_onSelectSlot',
      'calendar_onView',
      'calendar_onRangeChange',
      'dispatcher_event_onClose',
      'dispatcher_event_onCreate',
      'dispatcher_event_onSubmit',
      'dispatcher_event_onDelete',
      'dispatcher_event_onUpdate',
      'dispatcher_newEvent_onClose',
      'dispatcher_subscribe_onClick',
      'dispatcher_subscribe_onClose',
      'resourcesBar_onChange',
      'highlightTimeSlot',
      'setEventsContainerPosition',
      'filterTicketsByQueues',
      'getResourcesForSelect',
      'getCalendarEvents',
      'createEventOnDrag',
      'eventMove',
      'manageViewHours',
      'highlightWorkingHours',
      'updateTicket',
      'updateTicketStatus',
      'prepareTicketContextMenuItems',
      'prepareTicketContextMenuItemsForStatus',
      'displayTimesheetEntry',
      'getCcOptions',
      'getTimesheetTicket',
      'afterReloadCallback',
      'closeTimesheetForm',
      'updateEvent'
    ]

    each(handlerFunctions, (handlerFunction) => {
      this[handlerFunction] = this[handlerFunction].bind(component)
    })

    return this
  },

  calendar_onSelectEvent (event) {
    this.setState({ showEvent: event })
  },

  calendar_onSelectSlot (event) {
    this.setState({ newEvent: event })
  },

  calendar_onView (view) {
    const { calendarDefaults } = this.state

    calendarDefaults.currentView = view

    this.setState({ calendarDefaults })
  },

  calendar_onNavigate (date) {
    const { calendarDefaults } = this.state

    calendarDefaults.defaultDate = date

    this.setState({ calendarDefaults })
  },

  calendar_onRangeChange (dates) {
    const startDate = dates[0]
    const endDate = dates[dates.length - 1]
    this.handlers.getCalendarEvents(startDate, endDate)
    this.setState({ startDateView: startDate, endDateView: endDate })
  },

  dispatcher_event_onClose () {
    this.setState({ showEvent: null })
  },

  dispatcher_newEvent_onClose () {
    this.setState({ newEvent: null })
  },

  dispatcher_subscribe_onClick () {
    this.setState({ showSubscribe: true })
  },

  dispatcher_subscribe_onClose () {
    this.setState({ showSubscribe: false })
  },

  dispatcher_event_onSubmit (event) {
    const {
      title,
      description,
      start,
      end,
      createdBy,
      assignResource,
      workLocation
    } = event

    event.resourceId = event.resourceId || createdBy.id

    const params = {
      authenticity_token: csrfToken(),
      task: {
        name: title,
        description,
        estimation_time: parseTimeToEstimationTime(start, end),
        due_at: end,
        assignee_id: event.resourceId,
        taskable_type: event.ticket ? 'Ticket' : get(event, 'meta.taskable_type'),
        taskable_id: get(event, 'ticket.id') || get(event, 'meta.taskable_id'),
        work_location: workLocation
      },
      assign_resource: assignResource
    }

    this.setState({ processingEvent: true }, () => {
      if (event.meta) {
        this.handlers.dispatcher_event_onUpdate(event, params)
      } else {
        this.handlers.dispatcher_event_onCreate(event, params)
      }
    })
  },

  dispatcher_event_onCreate (event, params) {
    axios.post('/api/dispatcher/events', params)
      .then(({ data }) => {
        if (data.error_fields) {
          const { newEvent } = this.state
          const { error_fields, error_messages } = data

          this.setState({ newEvent: { ...newEvent, error_fields, error_messages } })
        } else {
          const { events, ticketsForSelect, filteredTickets } = this.state
          const { task } = params

          this.setState({
            events: concat(events, data.task),
            newEvent: null,
            reloadTicketsTable: true,
            displayForm: false
          })
        }
      })
      .catch((data) => {
        const { newEvent } = this.state

        this.setState({
          newEvent: {
            ...newEvent,
            error_fields: [undefined],
            error_messages: data.response.data.errors
          }
        })
      })
      .then(() => this.setState({ processingEvent: false }))
  },

  dispatcher_event_onUpdate (event, params) {

    axios.put(`/api/dispatcher/events/${event.meta.id}`, params)
      .then(({ data }) => {
        if (data.error_fields) {
          const { showEvent } = this.state
          const { error_fields, error_messages } = data

          this.setState({ showEvent: { ...showEvent, error_fields, error_messages } })
        } else {
          const { events } = this.state

          const updatedEvents = reject(events, e => e.meta.id === data.task.meta.id)

          this.setState({
            events: concat(updatedEvents, data.task),
            showEvent: null,
            reloadTicketsTable: true
          })
        }
      })
      .catch(({ data }) => {
        const { showEvent } = this.state

        this.setState({
          showEvent: {
            ...showEvent,
            error_fields: [undefined],
            error_messages: data.response.data.errors
          }
        })
      })
      .then(() => this.setState({ processingEvent: false }))
  },

  dispatcher_event_onDelete (event) {
    this.setState({ processingEvent: true }, () => {
      axios.delete(`/api/dispatcher/events/${event.meta.id}`, { params: { authenticity_token: csrfToken() } })
        .then(({ data }) => {
          const { events } = this.state
          const updatedEvents = reject(events, e => e.meta.id === data.task.meta.id)

          this.setState({ showEvent: null, events: updatedEvents })
        })
        .catch(() => console.log('Something went wrong when deleting event'))
        .then(() => this.setState({ processingEvent: false, showEvent: false }))
    })
  },

  resourcesBar_onChange (newValues) {
    const {
      eventsFilter,
      resources
    } = this.state

    eventsFilter.fetchFromResources(newValues)
    resources.updateDispatcher(newValues)
    resources.resourceBarValues = uniq(newValues)
    saveLocalStorageItem('Dispatcher.resources', 'barValues', newValues)

    this.setState({ eventsFilter, resources })
    this.handlers.getCalendarEvents(this.state.startDateView, this.state.endDateView)
    this.handlers.filterTicketsByQueues()
  },

  highlightTimeSlot (event, background) {
    event.persist()
    event.target.style.backgroundColor = background
    event.stopPropagation()
    event.preventDefault()
  },

  setEventsContainerPosition (position) {
    each(document.querySelectorAll('div.rbc-events-container'), (container) => {
      container.style.position = position
    })
  },

  filterTicketsByQueues () {
    const { resources: { resourceBarValues }, filtersToAdd } = this.state
    const selectedQueues = filter(resourceBarValues, resource => resource.type === 'queue')
    const queueFilters = map(selectedQueues, queue => (
      {
        title: 'Queue', subject: 'queue_id', type: 'is', value: queue.queue_id, label: queue.label.replace('Queue: ', '')
      }
    ))
    const filtersToDelete = difference(filtersToAdd, queueFilters)

    this.setState({ filtersToAdd: queueFilters, shouldAddQueueFilters: !isEmpty(queueFilters), filtersToDelete })
  },

  getResourcesForSelect () {
    const { resourcesEndpoint } = this.props

    this.setState({ processing: true }, () => {
      axios.get(resourcesEndpoint)
        .then(({ data: newResources }) => {
          const { resources } = this.state

          each(
            newResources,
            resource => resources.addToResourceBar(resource)
          )

          const queues = filter(newResources, resource => resource.type === 'TicketQueue')
          const users = flatMap(queues, queue => queue.users)

          this.setState({ resources, queues, users })
        })
        .then(() => this.setState({ processing: false }))
        .then(() => this.handlers.filterTicketsByQueues())
    })
  },

  getCalendarEvents (startDate = new Date(), endDate = new Date()) {
    const { eventsEndpoint } = this.props
    const { eventsFilter } = this.state

    if (eventsFilter.isEmpty()) {
      this.setState({ events: [] })
    } else {
      const params = {
        start_date: moment(startDate).format('YYYY-MM-DD'),
        end_date: moment(endDate).format('YYYY-MM-DD'),
        f: eventsFilter.toString()
      }

      this.setState({ processing: true }, () => {
        axios.get(eventsEndpoint, { params })
          .then(({ data }) => {
            const events = map(data, event => (
              {
                ...event,
                title: event.title,
                description: event.meta.notes,
                resourceId: event.meta.owner.owner_id
              }
            ))

            this.setState({ events })
          })
          .then(() => this.setState({ processing: false }))
      })
    }
  },

  // eslint-disable-next-line consistent-return
  highlightWorkingHours (slot, resourceId) {
    const humanize = object => (
      object.format('YYYY-MM-DD HH:mm')
    )
    const convertZone = (object, fromZone, toZone) => (
      humanize(momentZone.tz(object, fromZone).tz(toZone))
    )
    const { resources, currentUser } = this.props
    const resource = find(resources.dispatcher, ['resourceId', resourceId])
    const dayStart = humanize(moment(new Date(slot).setHours(9, 0)))
    const dayEnd = humanize(moment(new Date(slot).setHours(17, 0)))

    if (resource) {
      const resourceTimeZone = resource.resourceTimezone
      const range = moment.range(
        convertZone(dayStart, resourceTimeZone, currentUser.timezone),
        convertZone(dayEnd, resourceTimeZone, currentUser.timezone)
      )
      const workingHours = Array.from(range.by('minutes', { step: 15 }))
      const workingHoursArray = (map(workingHours, hour => (humanize(hour))))
      const formattedSlot = humanize(moment(slot))

      if (!includes(workingHoursArray, formattedSlot)) {
        return (
          'non-working-hour'
        )
      }
    }
  },

  createEventOnDrag (dragEvent, slot, resourceId) {
    dragEvent.persist()

    const { draggedTicket, policies } = this.state
    const { currentUser } = this.props
    const endSlot = moment(slot).add(15, 'm').toDate()

    const newCalendarEvent = {
      title: `Ticket #${draggedTicket.id} - ${draggedTicket.title}`,
      description: draggedTicket.description,
      createdBy: currentUser,
      ticket: draggedTicket,
      start: slot,
      end: endSlot,
      slots: [slot, endSlot],
      resourceId
    }

    if (policies.dispatcher.create) {
      this.setState({ newEvent: newCalendarEvent })
    } else {
      alert('You are not authorized to this action')
    }

    this.handlers.setEventsContainerPosition('absolute')
  },

  // eslint-disable-next-line class-methods-use-this
  eventMove (data) {
    const {
      start,
      end,
      resourceId,
      event
    } = data

    event.resourceId = resourceId
    event.start = start
    event.end = end

    this.handlers.dispatcher_event_onSubmit(event)
  },

  manageViewHours (action) {
    const { minHour, maxHour } = this.state

    if (action === 'up') {
      minHour.setHours(minHour.getHours() - 1)
      maxHour.setHours(maxHour.getHours() - 1)
    } else {
      maxHour.setHours(maxHour.getHours() + 1)
      minHour.setHours(minHour.getHours() + 1)
    }

    this.setState({ minHour, maxHour })
  },

  updateTicket (ticketId, params, type) {
    this.setState({ loadingTickets: true })

    cloudhelpPatch(`/tickets/${ticketId}`, params)
      .then((response) => {
        this.setState({ reloadTicketsTable: true })
      })
      .catch((error) => {
        const { data: errorData } = error.response
        const errors = map(errorData.errors[0], (value, key) => (
          `${humanizeString(key)} ${value[0]}`
        ))

        // eslint-disable-next-line no-alert
        alert(`Cannot update ticket.\n${map(errors, error => (`${error} `))}`)
      })
      .then(() => this.setState({
        loadingTickets: false,
        displayStatusExtraFields: false,
        statusTicketId: null,
        reopenOn: null,
        onHoldDescription: null,
        rootCause: null,
        preventiveAction: null,
        correctiveAction: null
      }))
  },

  updateTicketStatus (fields) {
    const {
      statusTicketId,
      selectedStatusId
    } = this.state

    const {
      reopenOn,
      onHoldDescription,
      rootCause,
      preventiveAction,
      correctiveAction
    } = fields

    const ticketData = {
      status_id: selectedStatusId,
      reopen_on: reopenOn,
      on_hold_description: onHoldDescription,
      root_cause: rootCause,
      preventive_action: preventiveAction,
      corrective_action: correctiveAction
    }

    this.handlers.updateTicket(statusTicketId, ticketData, null)
  },
  prepareTicketContextMenuItems (items, type) {
    return (
      map(sortBy(items, ['label']), item => (
        {
          label: item.label,
          value: item.value,
          handleClick: id => this.handlers.updateTicket(id, { [`${type}_id`]: item.value, [`${type}_text`]: item.label }, type)
        }
      ))
    )
  },

  prepareTicketContextMenuItemsForStatus (items, type) {
    return (
      map(sortBy(items, ['label']), (item) => {
        const updateStatus = id => this.handlers.updateTicket(id, { [`${type}_id`]: item.value, [`${type}_text`]: item.label }, type)
        const displayExtraFields = id => this.setState({
          displayStatusExtraFields: true, statusTicketId: id, selectedStatus: item.label, selectedStatusId: item.value
        })
        return (
          {
            label: item.label,
            value: item.value,
            handleClick: id => (item.inherit_parent_fields ? displayExtraFields(id) : updateStatus(id))
          }
        )
      })
    )
  },

  displayTimesheetEntry (state, event) {
    this.handlers.getTimesheetTicket(event.meta.taskable_id)
    this.setState({ timesheetOpen: state, timesheetEvent: event })
  },

  getTimesheetTicket (ticketId) {
    this.setState({ processing: true })
    cloudhelpGet(`/tickets/${ticketId}.json`)
      .then((response) => {
        const timesheetTicket = response.data
        this.setState({ timesheetTicket, processing: false }, () => this.handlers.getCcOptions(timesheetTicket.client_id))
      })
  },

  getCcOptions (clientId) {
    cloudhelpGet(`/api/clients/${clientId}/users?calligo_users=true`)
      .then((response) => {
        const newCcOptions = sortBy(map(response.data, user => ({ label: user.name, value: user.email })), 'label')
        this.setState(prevState => ({ ccOptions: newCcOptions }))
      })
  },

  afterReloadCallback () {
    this.setState({ reloadTicketsTable: false, shouldAddQueueFilters: false })
  },

  updateEvent (timesheetEvent) {
    const { events } = this.state

    cloudhelpGet(`/api/dispatcher/events/${timesheetEvent.meta.id}`)
      .then((response) => {
        const event = response.data.task
        events[indexOf(events, timesheetEvent)] = event

        this.setState({ events })
      })
  },

  closeTimesheetForm () {
    const { timesheetEvent } = this.state

    this.setState({ timesheetOpen: false, timesheetTicket: {}, processing: false })

    setTimeout(() => this.handlers.updateEvent(timesheetEvent), 2000)
  }
}
